<template>
    <Teleport to="body">
        <transition name="fade">
            <div
                v-if="isOpen"
                class="fixed inset-0 z-50 bg-white dark:bg-slate-800 flex flex-col"
                role="dialog"
                aria-labelledby="period-picker-mobile-title"
            >
                <header
                    class="flex items-center justify-between px-6 pt-6 pb-4"
                >
                    <h2
                        id="period-picker-mobile-title"
                        class="text-xl font-medium text-slate-800 dark:text-slate-100"
                    >
                        Select period
                    </h2>
                    <button
                        type="button"
                        class="btn-base btn-icon !m-0"
                        aria-label="Close"
                        @click="$emit('close')"
                    >
                        <Icon icon="x" class="text-slate-500 dark:text-slate-400" />
                    </button>
                </header>

                <div class="flex-1 overflow-y-auto px-6 pb-4">
                    <div class="flex flex-col gap-4">
                        <div class="flex items-center gap-2">
                            <button
                                type="button"
                                class="flex-1 px-3 py-2 border rounded text-sm text-slate-800 dark:text-slate-100 text-left transition-colors"
                                :class="
                                    focusedInput === 'start'
                                        ? 'border-slate-800 ring-1 ring-slate-800 dark:border-slate-300 dark:ring-slate-300'
                                        : 'border-slate-200 dark:border-slate-700'
                                "
                                @click="focusedInput = 'start'"
                            >
                                {{ formatYM(draftStart) }}
                            </button>
                            <span class="text-slate-400">–</span>
                            <button
                                type="button"
                                class="flex-1 px-3 py-2 border rounded text-sm text-slate-800 dark:text-slate-100 text-left transition-colors"
                                :class="
                                    focusedInput === 'end'
                                        ? 'border-slate-800 ring-1 ring-slate-800 dark:border-slate-300 dark:ring-slate-300'
                                        : 'border-slate-200 dark:border-slate-700'
                                "
                                @click="focusedInput = 'end'"
                            >
                                {{ formatYM(draftEnd) }}
                            </button>
                        </div>

                        <div class="flex gap-2 overflow-x-auto -mx-6 px-6 pb-1">
                            <button
                                v-for="preset in PRESETS"
                                :key="preset.value"
                                type="button"
                                class="shrink-0 px-3 py-2 text-sm rounded font-medium transition-colors whitespace-nowrap"
                                :class="
                                    draftPreset === preset.value
                                        ? 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-100'
                                        : 'border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800'
                                "
                                @click="selectPreset(preset.value)"
                            >
                                {{ preset.label }}
                            </button>
                        </div>

                        <hr class="border-slate-100 dark:border-slate-700" />

                        <div class="flex items-center justify-between px-2">
                            <button
                                type="button"
                                class="p-2 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700 rounded"
                                aria-label="Previous year"
                                @click="shiftYears(-1)"
                            >
                                <Icon
                                    icon="chevron-up"
                                    class="w-4 h-4 text-slate-800 dark:text-slate-100"
                                />
                            </button>
                            <span class="text-slate-500 dark:text-slate-400 text-xs">
                                {{ visibleYears[0] }} – {{ visibleYears[1] }}
                            </span>
                            <button
                                type="button"
                                :class="[
                                    'p-2 rounded',
                                    canShiftForward
                                        ? 'text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700'
                                        : 'text-slate-300 dark:text-slate-600 cursor-not-allowed',
                                ]"
                                :disabled="!canShiftForward"
                                aria-label="Next year"
                                @click="canShiftForward && shiftYears(1)"
                            >
                                <Icon icon="chevron-down" class="w-4 h-4" />
                            </button>
                        </div>

                        <div
                            v-for="year in visibleYears"
                            :key="`year-${year}`"
                            class="flex flex-col gap-3"
                        >
                            <h3
                                class="text-slate-800 dark:text-slate-100 text-base font-medium text-center"
                            >
                                {{ year }}
                            </h3>
                            <div class="grid grid-cols-3 gap-y-1">
                                <button
                                    v-for="(month, mIdx) in MONTHS"
                                    :key="`m-${year}-${mIdx}`"
                                    type="button"
                                    :class="getMonthClass(year, mIdx)"
                                    @click="clickMonth(year, mIdx)"
                                >
                                    {{ month }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <footer class="px-6 py-4 border-t border-slate-100 dark:border-slate-700">
                    <div class="flex gap-3">
                        <button
                            type="button"
                            class="btn-base btn-white flex-1"
                            @click="$emit('close')"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            class="btn-base btn-dark flex-1"
                            @click="apply"
                        >
                            Apply
                        </button>
                    </div>
                </footer>
            </div>
        </transition>
    </Teleport>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import dayjs from 'dayjs';
import { storeToRefs } from 'pinia';
import { useCardBenefitsStore } from '@/store/card-benefits';
import Icon from '@/components/ui/icon/icon.vue';

const props = defineProps({
    isOpen: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['close']);

const PRESETS = [
    { value: 'this-year', label: 'This year' },
    { value: 'previous-year', label: 'Previous year' },
    { value: 'this-month', label: 'This month' },
    { value: 'previous-month', label: 'Previous month' },
    { value: 'custom', label: 'Custom' },
];

const MONTHS = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
];

const store = useCardBenefitsStore();
const { insightsRangePreset, insightsDateRange } = storeToRefs(store);

const draftPreset = ref(insightsRangePreset.value);
const draftStart = ref({ year: 0, month: 0 });
const draftEnd = ref({ year: 0, month: 0 });
const visibleYears = ref([0, 0]);
const focusedInput = ref('start');

function ymFromDate(date) {
    const d = dayjs(date);
    return { year: d.year(), month: d.month() };
}

function loadFromStore() {
    const [start, end] = insightsDateRange.value;
    draftStart.value = ymFromDate(start);
    draftEnd.value = ymFromDate(end);
    draftPreset.value = insightsRangePreset.value;
    const currentYear = dayjs().year();
    visibleYears.value = [currentYear - 1, currentYear];
    focusedInput.value = 'start';
}

loadFromStore();

watch(insightsRangePreset, loadFromStore);

// Reset draft state every time the modal reopens so the user always starts
// from the persisted store values, never from a stale half-edited draft.
watch(
    () => props.isOpen,
    (open) => {
        if (open) {
            loadFromStore();
        }
    }
);

const today = computed(() => ymFromDate(new Date()));

const canShiftForward = computed(() => visibleYears.value[1] < today.value.year);

function isFuture(year, month) {
    const t = today.value;
    return year > t.year || (year === t.year && month > t.month);
}

function ymKey(ym) {
    return ym.year * 12 + ym.month;
}

function getMonthClass(year, month) {
    const cell = ymKey({ year, month });
    const start = ymKey(draftStart.value);
    const end = ymKey(draftEnd.value);
    const future = isFuture(year, month);

    const isStart = cell === start;
    const isEnd = cell === end;
    const inRange = cell > start && cell < end;

    const base = 'h-11 flex items-center justify-center text-sm transition-colors';

    if (isStart || isEnd) {
        return `${base} bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900 font-medium rounded`;
    }
    if (inRange) {
        return `${base} bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-100`;
    }
    if (future) {
        return `${base} text-slate-400 dark:text-slate-500 !cursor-not-allowed`;
    }
    return `${base} text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700 rounded cursor-pointer`;
}

function selectPreset(value) {
    draftPreset.value = value;
    focusedInput.value = 'start';

    if (value === 'custom') {
        return;
    }

    const now = dayjs();
    if (value === 'this-year') {
        draftStart.value = { year: now.year(), month: 0 };
        draftEnd.value = { year: now.year(), month: now.month() };
    } else if (value === 'previous-year') {
        const ref = now.subtract(1, 'year');
        draftStart.value = { year: ref.year(), month: 0 };
        draftEnd.value = { year: ref.year(), month: 11 };
    } else if (value === 'this-month') {
        draftStart.value = { year: now.year(), month: now.month() };
        draftEnd.value = { year: now.year(), month: now.month() };
    } else if (value === 'previous-month') {
        const ref = now.subtract(1, 'month');
        draftStart.value = { year: ref.year(), month: ref.month() };
        draftEnd.value = { year: ref.year(), month: ref.month() };
    }
}

function clickMonth(year, month) {
    if (isFuture(year, month)) return;

    draftPreset.value = 'custom';
    const ym = { year, month };

    if (focusedInput.value === 'start') {
        draftStart.value = ym;
        if (ymKey(ym) > ymKey(draftEnd.value)) {
            draftEnd.value = ym;
        }
        focusedInput.value = 'end';
        return;
    }

    if (focusedInput.value === 'end') {
        if (ymKey(ym) < ymKey(draftStart.value)) {
            draftEnd.value = draftStart.value;
            draftStart.value = ym;
        } else {
            draftEnd.value = ym;
        }
        focusedInput.value = 'start';
    }
}

function shiftYears(direction) {
    visibleYears.value = [
        visibleYears.value[0] + direction,
        visibleYears.value[1] + direction,
    ];
}

function formatYM(ym) {
    if (!ym || ym.year === 0) return '';
    return `${MONTHS[ym.month]} ${ym.year}`;
}

function apply() {
    if (draftPreset.value === 'custom') {
        store.setInsightsCustomRange({
            startYear: draftStart.value.year,
            startMonth: draftStart.value.month,
            endYear: draftEnd.value.year,
            endMonth: draftEnd.value.month,
        });
    } else {
        store.setInsightsRangePreset(draftPreset.value);
    }
    emit('close');
}
</script>
